/* eslint-disable no-console */

import { useEffect, useState } from "react";
import { ButtonGroup } from "@mui/material";
import ConfirmDialog from "../ConfirmDialog";
import {
	unsubscribePlaygamiPush,
	getPlaygamiNotificationsEnabled
} from "services/push/playgamiPushService";
import { usePushNotificationsContext } from "contexts/PushNotificationsProvider";
import {
	PUSH_LOCAL_STATE_KEY,
	PWARewardResponse,
	PWARewardStatusResponse
} from "interfaces/pwa";
import { pwaAPI } from "redux/apis/pwa";
import { safeLocalStorageSetItem } from "utils/safeLocalStorage";

const PWATools = () => {
	const pwaCtx = usePushNotificationsContext();
	const { pushLocalState } = pwaCtx;

	const [uiPushNotifications, setUiPushNotifications] = useState(() => ({
		isSupported: pwaCtx?.isSupported,
		isSubscribed: pwaCtx?.isSubscribed,
		permission: pwaCtx?.permission
	}));
	const [uiPushLocalState, setUiPushLocalState] = useState(pushLocalState);
	const [hasBrowserSubscription, setHasBrowserSubscription] = useState<
		"checking" | "true" | "false" | "error"
	>("checking");

	useEffect(() => {
		setUiPushNotifications({
			isSupported: pwaCtx?.isSupported,
			isSubscribed: pwaCtx?.isSubscribed,
			permission: pwaCtx?.permission
		});
	}, [pwaCtx?.isSupported, pwaCtx?.isSubscribed, pwaCtx?.permission]);

	useEffect(() => {
		setUiPushLocalState(pushLocalState);
	}, [pushLocalState]);

	useEffect(() => {
		const checkBrowserSubscription = async () => {
			try {
				if (
					typeof navigator === "undefined" ||
					!("serviceWorker" in navigator)
				) {
					setHasBrowserSubscription("error");
					return;
				}

				// Try primary registration
				try {
					const readyReg = await navigator.serviceWorker.ready;
					const readySub = await readyReg.pushManager.getSubscription();
					if (readySub) {
						setHasBrowserSubscription("true");
						return;
					}
				} catch (error) {
					// console.log("[PWATools] Failed checking ready registration", error);
				}

				// Try all registrations as fallback
				try {
					const registrations =
						await navigator.serviceWorker.getRegistrations();
					for (const reg of registrations) {
						const sub = await reg.pushManager.getSubscription();
						if (sub) {
							setHasBrowserSubscription("true");
							return;
						}
					}
				} catch (error) {
					// console.log("[PWATools] Failed checking all registrations", error);
				}

				// No subscription found
				setHasBrowserSubscription("false");
			} catch (error) {
				// console.log("[PWATools] Error checking browser subscription", error);
				setHasBrowserSubscription("error");
			}
		};

		checkBrowserSubscription();
		// Recheck every 2 seconds to catch changes
		const interval = setInterval(checkBrowserSubscription, 2000);
		return () => clearInterval(interval);
	}, []);

	const hasPushLocalState =
		uiPushLocalState != null && Object.keys(uiPushLocalState).length > 0;

	const { data: pwaReward }: { data?: PWARewardResponse } =
		pwaAPI.endpoints.getRewardPWA.useQueryState("");
	const { data: pwaRewardStatus }: { data?: PWARewardStatusResponse } =
		pwaAPI.endpoints.getRewardStatusPWA.useQueryState("");
	const rewardConfigured = Boolean(pwaReward?.rewardId);
	const rewardGranted = Boolean(pwaRewardStatus?.pwaReward?.rewardId);

	return (
		<>
			<div style={{ padding: "0rem 0.2rem" }}>Reward:</div>
			<div style={{ fontSize: "0.8rem", marginBottom: "0.25rem" }}>
				<div style={{ padding: "0.5rem" }}>
					configured: <b>{String(rewardConfigured)}</b>
				</div>
				<div style={{ padding: "0.5rem" }}>
					granted: <b>{String(rewardGranted)}</b>
				</div>
			</div>
			<div style={{ marginTop: "0.5rem", padding: "0.2rem" }}>
				Push Notifications:
			</div>
			<div style={{ fontSize: "0.8rem", marginBottom: "0.25rem" }}>
				<div style={{ padding: "0.5rem" }}>
					isSupported: <b>{String(uiPushNotifications.isSupported)}</b>
				</div>
				<div style={{ padding: "0.5rem" }}>
					permission: <b>{uiPushNotifications.permission}</b>
				</div>
				<div style={{ padding: "0.5rem" }}>
					isSubscribed: <b>{String(uiPushNotifications.isSubscribed)}</b>
				</div>
				<div style={{ padding: "0.5rem" }}>
					hasBrowserSubscription: <b>{String(hasBrowserSubscription)}</b>
				</div>
			</div>
			<div>
				<ButtonGroup>
					<ConfirmDialog
						buttonText={"Unsubscribe from push"}
						onClick={async () => {
							await unsubscribePlaygamiPush();
							// console.log(
							// 	"[Push] Debug: called unsubscribePlaygamiPush from PWATools"
							// );
							const enabled = getPlaygamiNotificationsEnabled();
							// console.log(
							// 	"[Push] Debug: playgami notificationsEnabled after unsubscribe",
							// 	enabled
							// );
							setUiPushNotifications((prev) => ({
								...prev,
								isSubscribed: enabled
							}));
						}}
					/>
				</ButtonGroup>
			</div>
			<br />

			<div style={{ padding: "0rem 0.5rem", marginBottom: "0.25rem" }}>
				<div style={{ fontSize: "0.8rem" }}>Push local state:</div>
				{!hasPushLocalState ? (
					<div style={{ fontSize: "0.7rem", fontStyle: "italic" }}>empty</div>
				) : (
					<pre
						style={{
							fontSize: "0.7rem",
							whiteSpace: "pre-wrap",
							wordBreak: "break-all",
							margin: 0
						}}
					>
						{JSON.stringify(uiPushLocalState, null, 2)}
					</pre>
				)}
			</div>
			<div>
				<ButtonGroup>
					<ConfirmDialog
						buttonText={"Clear Push local state"}
						onClick={() => {
							safeLocalStorageSetItem(PUSH_LOCAL_STATE_KEY, JSON.stringify({}));
							setUiPushLocalState(null);
						}}
					/>
				</ButtonGroup>
			</div>
		</>
	);
};

export default PWATools;
